import React, { useEffect, useState } from 'react'
import styled, { keyframes } from 'styled-components'
import { Dialog, Tabs, Tab } from '@mui/material'
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined'
import LinkOffOutlinedIcon from '@mui/icons-material/LinkOffOutlined'
import { toast } from 'react-toastify'
import Avatar from 'components/Avatar'
import SearchableSelect from 'components/SearchableSelect'
import * as estateDocumentsService from 'services/estateDocumentsService'
import { hideEmail } from 'utils/user'
import { getApiErrors } from 'utils/errors'
import { font, ink, line, radius, surface } from 'features/dashboard/tokens'

const CYAN = '#33becc'

const ACCESS_LEVEL_OPTIONS = [
  { value: 'view', label: 'View only' },
  { value: 'edit', label: 'View & edit' }
]

const EstateShareFileModal = ({ estateId, file, buildingPassports = [], onClose, onChanged }) => {
  const displayName = file.displayName || file.display_name || file.name || 'this document'
  const [tab, setTab] = useState(0)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)

  const [external, setExternal] = useState([])
  const [email, setEmail] = useState('')
  const [accessLevel, setAccessLevel] = useState('view')

  const [addedBpIds, setAddedBpIds] = useState([])
  const [selectedBpIds, setSelectedBpIds] = useState([])

  const loadShares = () => {
    setLoading(true)
    return Promise.all([
      estateDocumentsService.listEstateFileSharedUsers({ estateId, fileId: file.id }),
      estateDocumentsService.listEstateFileBuildingShares({ estateId, fileId: file.id })
    ])
      .then(([sharesRes, bpRes]) => {
        setExternal(sharesRes.data?.data?.external || [])
        setAddedBpIds((bpRes.data?.data || []).map(s => s.buildingPassportId || s.building_passport_id))
      })
      .catch(() => toast.error('Could not load how this document is shared'))
      .finally(() => setLoading(false))
  }

  useEffect(() => { loadShares() }, [])

  const submitEmail = async () => {
    const value = email.trim()
    if (!value || !value.includes('@')) { toast.error('Enter a valid email address'); return }
    setBusy(true)
    try {
      await estateDocumentsService.shareEstateFile({ estateId, fileId: file.id, email: value, accessLevel })
      setEmail('')
      await loadShares()
      toast.success(`Secure link sent to ${value}`)
    } catch (error) {
      const apiError = getApiErrors(error)
      toast.error(apiError?.trim() ? apiError : 'Failed to share the document')
    } finally {
      setBusy(false)
    }
  }

  const revokeExternal = async (shareId) => {
    try {
      await estateDocumentsService.revokeEstateFileExternalShare({ estateId, fileId: file.id, shareId })
      await loadShares()
    } catch (error) {
      toast.error('Could not revoke the link')
    }
  }

  const copyLink = (link) => {
    navigator.clipboard?.writeText(link)
    toast.success('Link copied')
  }

  const bpOptions = buildingPassports.map(bp => {
    const added = addedBpIds.includes(bp.id)
    return {
      value: bp.id,
      label: bp.name || bp.address || 'Building Passport',
      disabled: added,
      sublabel: added ? 'Already added' : undefined
    }
  })

  const submitBps = async () => {
    if (selectedBpIds.length === 0) { toast.error('Select at least one Building Passport'); return }
    setBusy(true)
    try {
      const { data } = await estateDocumentsService.shareEstateFileWithBuildingPassports({
        estateId,
        fileId: file.id,
        buildingPassportIds: selectedBpIds
      })
      const sharedTo = data?.data?.sharedTo ?? 0
      toast.success(`Added to ${sharedTo} Building Passport${sharedTo === 1 ? '' : 's'}`)
      setSelectedBpIds([])
      await loadShares()
      onChanged && onChanged()
    } catch (error) {
      const apiError = getApiErrors(error)
      toast.error(apiError?.trim() ? apiError : 'Failed to add to Building Passports')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open onClose={busy ? undefined : onClose} maxWidth='sm' fullWidth>
      <Container>
        <Title title={displayName}>Share "{displayName}"</Title>

        <Tabs
          value={tab}
          onChange={(event, value) => setTab(value)}
          sx={{
            minHeight: 36,
            '& .MuiTab-root': { minHeight: 36, textTransform: 'none', fontWeight: 600, fontSize: 13 },
            '& .Mui-selected': { color: CYAN },
            '& .MuiTabs-indicator': { backgroundColor: CYAN }
          }}
        >
          <Tab label={`External link${external.length ? ` (${external.length})` : ''}`} />
          <Tab label='Add to Building Passports' />
        </Tabs>

        {loading ? (
          <SkeletonRows>
            {Array.from({ length: 3 }).map((_, index) => <SkeletonLine key={index} />)}
          </SkeletonRows>
        ) : tab === 0 ? (
          <TabBody>
            <Hint>Send a secure link to someone outside the organisation. They won't need an account.</Hint>
            <EmailRow>
              <EmailInput
                type='email'
                value={email}
                placeholder='Recipient email'
                onChange={event => setEmail(event.target.value)}
                onKeyDown={event => { if (event.key === 'Enter') submitEmail() }}
              />
              <AccessLevelField>
                <SearchableSelect
                  value={accessLevel}
                  onChange={setAccessLevel}
                  options={ACCESS_LEVEL_OPTIONS}
                  searchable={false}
                  ariaLabel='Permission'
                />
              </AccessLevelField>
              <SendButton type='button' onClick={submitEmail} disabled={busy}>
                {busy ? 'Sending…' : 'Send'}
              </SendButton>
            </EmailRow>

            {external.length === 0 ? (
              <Empty>This document hasn't been shared by link yet.</Empty>
            ) : (
              <ShareList>
                {external.map(s => (
                  <ShareRow key={s.id}>
                    <Avatar email={s.email} size={34} />
                    <ShareInfo>
                      <ShareEmail>{hideEmail(s.email)}</ShareEmail>
                      <ShareMeta>{s.accessLevel === 'edit' ? 'View & edit' : 'View only'}</ShareMeta>
                    </ShareInfo>
                    <RowActions>
                      <RowIconButton type='button' title='Copy link' onClick={() => copyLink(s.link)}>
                        <ContentCopyOutlinedIcon style={{ fontSize: 16 }} />
                      </RowIconButton>
                      <RowIconButton type='button' title='Revoke link' onClick={() => revokeExternal(s.id)}>
                        <LinkOffOutlinedIcon style={{ fontSize: 16 }} />
                      </RowIconButton>
                    </RowActions>
                  </ShareRow>
                ))}
              </ShareList>
            )}
          </TabBody>
        ) : (
          <TabBody>
            <Hint>
              Add a copy of this document to one or more Building Passports in this estate. It will appear
              in each Building Passport's own files.
            </Hint>
            {buildingPassports.length === 0 ? (
              <Empty>There are no Building Passports in this estate yet.</Empty>
            ) : (
              <Field>
                <FieldLabel htmlFor='estate-share-bps'>Building Passports</FieldLabel>
                <SearchableSelect
                  id='estate-share-bps'
                  value={selectedBpIds}
                  onChange={setSelectedBpIds}
                  options={bpOptions}
                  multiple
                  placeholder='Choose Building Passports…'
                  searchPlaceholder='Search Building Passports…'
                  ariaLabel='Building Passports to add this document to'
                />
              </Field>
            )}
          </TabBody>
        )}

        <Footer>
          <CancelButton type='button' onClick={onClose}>Close</CancelButton>
          {tab === 1 && buildingPassports.length > 0 && (
            <SaveButton type='button' onClick={submitBps} disabled={busy || selectedBpIds.length === 0}>
              {busy ? 'Adding…' : `Add to ${selectedBpIds.length || ''} Building Passport${selectedBpIds.length === 1 ? '' : 's'}`.trim()}
            </SaveButton>
          )}
        </Footer>
      </Container>
    </Dialog>
  )
}

export default EstateShareFileModal

const skeletonPulse = keyframes`
  0%, 100% { opacity: 0.55; }
  50%      { opacity: 1; }
`

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 24px;
  background: ${surface.card};
`

const Title = styled.h2`
  margin: 0;
  font-size: ${font.heading};
  font-weight: 700;
  color: ${ink.base};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const TabBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const Hint = styled.p`
  margin: 0;
  font-size: ${font.body};
  color: ${ink.mute};
  line-height: 1.5;
`

const EmailRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;

  @media (max-width: 768px) {
    flex-wrap: wrap;
  }
`

const EmailInput = styled.input`
  flex: 1;
  min-width: 0;
  min-height: 40px;
  padding: 9px 12px;
  border: 1px solid ${line.strong};
  border-radius: ${radius.sm};
  background: ${surface.card};
  color: ${ink.base};
  font-size: ${font.body};
  outline: none;
  box-sizing: border-box;

  &:focus {
    border-color: ${CYAN};
  }
`

const AccessLevelField = styled.div`
  flex: 0 0 160px;

  @media (max-width: 768px) {
    flex: 1 1 160px;
  }
`

const SendButton = styled.button`
  flex-shrink: 0;
  min-height: 40px;
  padding: 0 18px;
  border: none;
  border-radius: ${radius.sm};
  background: ${CYAN};
  color: #ffffff;
  font-size: ${font.body};
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: background 160ms ease, opacity 160ms ease;

  &:hover:not(:disabled) {
    background: #2aa6b3;
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`

const Empty = styled.p`
  margin: 0;
  padding: 10px 0;
  font-size: ${font.body};
  color: ${ink.mute};
`

const ShareList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  max-height: 240px;
  overflow-y: auto;
`

const ShareRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 4px;
  border-bottom: 1px solid ${line.base};

  &:last-child {
    border-bottom: none;
  }
`

const ShareInfo = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
`

const ShareEmail = styled.span`
  font-size: ${font.body};
  font-weight: 600;
  color: ${ink.base};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const ShareMeta = styled.span`
  font-size: ${font.small};
  color: ${ink.mute};
`

const RowActions = styled.div`
  display: flex;
  gap: 2px;
  flex-shrink: 0;
`

const RowIconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: ${radius.xs};
  background: transparent;
  color: ${ink.mute};
  cursor: pointer;
  transition: background 120ms ease, color 120ms ease;

  &:hover {
    background: rgba(51, 190, 204, 0.12);
    color: #2aa6b3;
  }

  &:focus-visible {
    outline: 2px solid ${CYAN};
    outline-offset: 1px;
  }
`

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`

const FieldLabel = styled.label`
  font-size: ${font.caption};
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${ink.mute};
`

const SkeletonRows = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 8px 0;
`

const SkeletonLine = styled.span`
  display: block;
  height: 18px;
  border-radius: ${radius.xs};
  background: #e7e9ee;
  animation: ${skeletonPulse} 1.3s ease-in-out infinite;
`

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
`

const CancelButton = styled.button`
  min-height: 40px;
  padding: 0 18px;
  border: 1px solid ${line.strong};
  border-radius: ${radius.sm};
  background: ${surface.card};
  color: ${ink.soft};
  font-size: ${font.body};
  font-weight: 600;
  cursor: pointer;
  transition: color 160ms ease;

  &:hover {
    color: ${ink.base};
  }

  &:focus-visible {
    outline: 2px solid ${CYAN};
    outline-offset: 1px;
  }
`

const SaveButton = styled.button`
  min-height: 40px;
  padding: 0 22px;
  border: none;
  border-radius: ${radius.sm};
  background: ${CYAN};
  color: #ffffff;
  font-size: ${font.body};
  font-weight: 600;
  cursor: pointer;
  transition: background 160ms ease, opacity 160ms ease;

  &:hover:not(:disabled) {
    background: #2aa6b3;
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`
